import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CommandPalette, type CommandItem } from '@teals/ui-web';
import { useThemeToggle } from '../hooks/useThemeToggle';
import { useAuth } from '../hooks/useAuth';
import { useCommandPalette } from '../hooks/useCommandPalette';

/* ---------- navigation items ---------- */
const NAV_ITEMS = [
  { to: '/agents', label: 'Agents' },
  { to: '/integrations', label: 'Integrations' },
  { to: '/apps', label: 'Apps' },
];

/* ---------- styled components ---------- */
const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--nav-bg);
  border-bottom: 1px solid var(--nav-border);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 1.5rem;
  max-width: 1440px;
  margin: 0 auto;
`;

const NavBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 700;
  font-size: 1.25rem;
  color: var(--text-primary);
`;

const LogoIcon = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledNavLink = styled(NavLink)`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
  text-decoration: none;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
    text-decoration: none;
  }

  &.active {
    background: var(--accent-subtle);
    color: var(--accent);
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CmdTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-muted);
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 220px;
  font-family: inherit;

  &:hover {
    border-color: var(--accent);
    color: var(--text-secondary);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Kbd = styled.kbd`
  font-family: inherit;
  font-size: 0.6875rem;
  padding: 2px 6px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  margin-left: auto;
  color: var(--text-muted);
`;

const ThemeToggleBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1.125rem;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const UserMenuWrapper = styled.div`
  position: relative;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
`;

const UserDropdown = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'block' : 'none')};
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 200px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-lg);
  z-index: 200;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    background: var(--bg-elevated);
    color: var(--text-primary);
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: var(--border);
`;

const PageBody = styled.main`
  flex: 1;
`;

/* ---------- component ---------- */
const AppLayout: React.FC = () => {
  const { mode, toggle } = useThemeToggle();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isOpen, open, close } = useCommandPalette();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const paletteItems: CommandItem[] = useMemo(
    () => [
      ...NAV_ITEMS.map((item) => ({
        id: item.to,
        label: item.label,
        description: `Go to ${item.label}`,
        group: 'Navigation',
      })),
      {
        id: '/business',
        label: 'My Business Center',
        description: 'Organization management',
        group: 'Navigation',
      },
    ],
    [],
  );

  const handleSelect = (item: CommandItem) => {
    navigate(item.id);
    close();
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  return (
    <>
      <Nav>
        <NavInner>
          <NavBrand>
            <LogoIcon>
              <svg viewBox="0 0 32 32" width="32" height="32" xmlns="http://www.w3.org/2000/svg">
                <rect width="32" height="32" rx="7" fill="var(--accent)" />
                <path d="M16 4Q16 16 4 16Q16 16 16 28Q16 16 28 16Q16 16 16 4Z" fill="white" />
                <circle cx="24" cy="7" r="1.8" fill="white" opacity="0.6" />
              </svg>
            </LogoIcon>
            Lumina
          </NavBrand>

          <NavLinks>
            {NAV_ITEMS.map((item) => (
              <StyledNavLink key={item.to} to={item.to}>
                {item.label}
              </StyledNavLink>
            ))}
          </NavLinks>

          <NavActions>
            <CmdTrigger onClick={open}>
              <span>&#x2728;</span> Ask Lumina...
              <Kbd>&#x2318;K</Kbd>
            </CmdTrigger>
            <ThemeToggleBtn
              onClick={toggle}
              aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
              title="Toggle theme"
            >
              {mode === 'light' ? '\u263E' : '\u2600'}
            </ThemeToggleBtn>
            <UserMenuWrapper ref={menuRef}>
              <UserAvatar
                onClick={() => setMenuOpen((prev) => !prev)}
                title="Account menu"
              >
                {user?.name
                  ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
                  : 'U'}
              </UserAvatar>
              <UserDropdown $open={menuOpen}>
                <DropdownItem
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/business');
                  }}
                >
                  My Business Center
                </DropdownItem>
                <DropdownDivider />
                <DropdownItem onClick={handleLogout}>Log Out</DropdownItem>
              </UserDropdown>
            </UserMenuWrapper>
          </NavActions>
        </NavInner>
      </Nav>

      <PageBody>
        <Outlet />
      </PageBody>

      <CommandPalette
        isOpen={isOpen}
        onClose={close}
        onSelect={handleSelect}
        items={paletteItems}
        placeholder="Search pages..."
      />
    </>
  );
};

export default AppLayout;
